import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { isMissedLinkClosed } from '@/domain/missedLinks';
import type { MissedLinkCloseOutcome } from '@/domain/types';

// ---------------------------------------------------------------------------
// Explicit close-off of an unpaid deposit link (W5 mg, board 08 "re-call to
// close it out"). The Sweeper (its own missed-deposits list) or Ops (the
// unclosed-links tab) records an outcome + a note; the link then leaves every
// list (missedLinks treats `missedClosedAt` as closed). The deposit persists
// through the write-through bridge (deposits.missed_closed_*).
// ---------------------------------------------------------------------------

export interface CloseOffMissedLinkInput {
  depositId: string;
  outcome: MissedLinkCloseOutcome;
  /** Mandatory: what happened. */
  note: string;
  /** Sweeper or Ops person id. */
  by: string;
  at?: string;
}

export type CloseOffMissedLinkResult =
  | { ok: true; depositId: string }
  | { ok: false; error: 'no-deposit' | 'missing-note' | 'already-closed' };

export function closeOffMissedLink(input: CloseOffMissedLinkInput): CloseOffMissedLinkResult {
  const note = input.note.trim();
  if (!note) return { ok: false, error: 'missing-note' };
  const brain = useBrainStore.getState();
  const d = brain.deposits[input.depositId];
  if (!d) return { ok: false, error: 'no-deposit' };
  if (isMissedLinkClosed(d, brain.bookings)) return { ok: false, error: 'already-closed' };
  const at = input.at ?? useSimulationStore.getState().clockIso;
  brain.upsertDeposit({
    ...d,
    missedClosedAt: at,
    missedClosedBy: input.by,
    missedClosedOutcome: input.outcome,
    missedClosedNote: note,
  });
  brain.appendActivity({
    id: `act-link-closed-${d.id}`,
    type: 'deposit-expired',
    customerId: d.customerId,
    studioId: d.studioId,
    ...(d.artistId ? { artistId: d.artistId } : {}),
    amount: d.amountAud,
    body: `Unpaid deposit link closed off by ${input.by} (${input.outcome}): ${note}`,
    timestamp: at,
  });
  return { ok: true, depositId: d.id };
}
